/**
 * @module NotificationPreferencesForm
 * @package @geenius/notifications-react
 * @description Renders the shared preference controls for the Tailwind React
 * variant. The form exposes accessible channel, type, and quiet-hour controls
 * that can be embedded in panels or full-page settings screens.
 */

import { motion } from '@geenius/motion/react'
import type {
  NotificationChannel,
  NotificationPreferences,
  NotificationType,
} from '@geenius/notifications/shared'
import {
  getNotificationChannelLabel,
  getNotificationTypeLabel,
  NOTIFICATION_TYPES,
  TYPE_CONFIG,
} from '@geenius/notifications/shared'
import type { JSX } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import { cn } from '../lib/utils'

/**
 * Async or sync handler used by the preferences form.
 */
export type NotificationPreferencesFormUpdate = (
  channels?: Record<NotificationChannel, boolean>,
  types?: Record<NotificationType, boolean>,
  quietHours?: { start: string; end: string },
) => void | Promise<void>

/**
 * Props for the React preferences form.
 */
export interface NotificationPreferencesFormProps {
  preferences: NotificationPreferences
  onUpdate: NotificationPreferencesFormUpdate
  className?: string
}

const CHANNELS: NotificationChannel[] = ['in-app', 'email', 'push', 'sms']

/**
 * Renders the notification preferences form.
 *
 * @param props Preference data and update handler.
 * @returns The preference form markup.
 */
export function NotificationPreferencesForm({
  preferences,
  onUpdate,
  className,
}: NotificationPreferencesFormProps): JSX.Element {
  const { t } = useNotificationCopy()
  const quietHours = preferences.quietHours ?? { start: '22:00', end: '07:00' }

  return (
    <form
      className={cn('flex flex-col gap-6', className)}
      aria-label={t('notifications.preferences.formAria')}
    >
      <fieldset>
        <legend className="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.channels')}
        </legend>
        <div className="flex flex-col gap-2">
          {CHANNELS.map((channel) => {
            const inputId = `notifications-channel-${channel}`
            const channelLabel = getNotificationChannelLabel(channel, t)

            return (
              <motion.label
                key={channel}
                htmlFor={inputId}
                whileHover={{ x: 2 }}
                className="group flex cursor-pointer items-center justify-between rounded-xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-4 py-3 transition-colors hover:bg-[var(--gn-notifications-panel-surface)]"
              >
                <span className="text-sm font-medium text-foreground transition-colors group-hover:text-white">
                  {channelLabel}
                </span>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={preferences.channels[channel]}
                  onChange={(event) =>
                    void onUpdate({
                      ...preferences.channels,
                      [channel]: event.target.checked,
                    })
                  }
                  aria-label={t('notifications.preferences.toggleChannelAria', {
                    channel: channelLabel,
                  })}
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
              </motion.label>
            )
          })}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.types')}
        </legend>
        <div className="flex flex-col gap-2">
          {NOTIFICATION_TYPES.map((type) => {
            const inputId = `notifications-type-${type}`
            const typeLabel = getNotificationTypeLabel(type, t)

            return (
              <motion.label
                key={type}
                htmlFor={inputId}
                whileHover={{ x: 2 }}
                className="group flex cursor-pointer items-center justify-between rounded-xl border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-surface-muted)] px-4 py-3 transition-colors hover:bg-[var(--gn-notifications-panel-surface)]"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-foreground transition-colors group-hover:text-white">
                  {TYPE_CONFIG[type].icon}
                  <span>{typeLabel}</span>
                </span>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={preferences.types[type]}
                  onChange={(event) =>
                    void onUpdate(undefined, {
                      ...preferences.types,
                      [type]: event.target.checked,
                    })
                  }
                  aria-label={t('notifications.preferences.toggleTypeAria', {
                    type: typeLabel,
                  })}
                  className="h-4 w-4 cursor-pointer accent-primary"
                />
              </motion.label>
            )
          })}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-3 text-[13px] font-bold uppercase tracking-wide text-foreground">
          {t('notifications.preferences.sections.quietHours')}
        </legend>
        <div className="flex items-center gap-3">
          <label
            className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            htmlFor="notifications-quiet-start"
          >
            {t('notifications.preferences.fields.start')}
            <input
              id="notifications-quiet-start"
              type="time"
              value={quietHours.start}
              onChange={(event) =>
                void onUpdate(undefined, undefined, {
                  start: event.target.value,
                  end: quietHours.end,
                })
              }
              className="rounded-lg border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-backdrop)] px-3 py-2 text-sm text-foreground transition-colors focus:border-primary/50 focus:outline-none"
            />
          </label>
          <label
            className="flex flex-col gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
            htmlFor="notifications-quiet-end"
          >
            {t('notifications.preferences.fields.end')}
            <input
              id="notifications-quiet-end"
              type="time"
              value={quietHours.end}
              onChange={(event) =>
                void onUpdate(undefined, undefined, {
                  start: quietHours.start,
                  end: event.target.value,
                })
              }
              className="rounded-lg border border-[var(--gn-notifications-panel-border)] bg-[var(--gn-notifications-panel-backdrop)] px-3 py-2 text-sm text-foreground transition-colors focus:border-primary/50 focus:outline-none"
            />
          </label>
        </div>
      </fieldset>
    </form>
  )
}
